import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import {
  Box,
  Button,
  CircularProgress,
} from '@mui/material';

import { useThemeColors } from '../../../hooks/useThemeColors';
import {
  UseCreateDeliveryManager,
  useEditDeliveryManager,
} from '../../Query/DeliveryManagerQuery';
import { useAtom } from 'jotai';
import { deliveryManagerAtom } from './AdminDeliveryManagerTable';
import { useState } from 'react';
import PhoneNumberInput from '../Utils/PhoneNumberInput';
import { FieldInputProps, FormikProps } from 'formik';
import ImageUploadField from '../Utils/ImageUploadField';
import { splitPhoneNumber } from '../../HelperFunctions/validationUtils';

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Please enter Name ')
    .matches(/^[A-Za-z\s]+$/, 'Name should only contain letters and spaces')
    .max(100, 'Item Name cannot exceed 50 characters')
    .test(
      'not-only-spaces',
      'Name cannot be only spaces',
      (value) => {
        if (typeof value !== 'string') return false;
        return value.trim().length > 0;
      }
    ),
  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/,
      'Please enter a valid email address'
    )
    .required('Email is required'),
  mobile: Yup.string()
    .required('Please enter the mobile number')
    .matches(/^[0-9]+$/, 'Mobile number should contain only numbers')
    .test(
      'len',
      'Mobile number must be 9 digits or more',
      (val) => val?.length >= 9
    )
    .notOneOf(['0000000000'], 'Mobile number cannot be all zeroes'),
  profilePhoto: Yup.mixed()
    // .required('Restaurant logo is required')
    .test('fileSize', 'Logo must be less than 500kb', (value: any) => {
      if (!value || typeof value === 'string') return true;
      return value.size <= 500000;
    })
    .test(
      'dimensions',
      'Logo must be between 300x300px and 500x500px',
      (value: any): Promise<boolean> => {
        if (!value || !(value instanceof File)) return Promise.resolve(true);

        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const img = new Image();
            img.src = e.target?.result as string;

            img.onload = () => {
              const { width, height } = img;
              if (
                width >= 300 &&
                height >= 300 &&
                width <= 500 &&
                height <= 500
              ) {
                resolve(true);
              } else {
                resolve(false);
              }
            };

            img.onerror = () => resolve(false);
          };
          reader.readAsDataURL(value);
        });
      }
    ),
});



const EditDeliveryManager = ({ onCancel }: { onCancel: () => void }) => {
  const [DeliveryManagerData] = useAtom(deliveryManagerAtom);
  const { warning } = useThemeColors();



  // const fullNumber = DeliveryManagerData?.mobileNumber;
  // const countryCode = String(fullNumber)?.slice(0, -10);
  
    const {countryCode} = splitPhoneNumber(DeliveryManagerData?.mobileNumber as string)

  const [selectedCountryCode, setSelectedCountryCode] = useState(countryCode);
  const {
    mutate: CreateDeliveryManager,
    isPending: CreateDeliveryManagerLoading,
  } = UseCreateDeliveryManager();

  const { mutate: EditDeliveryManager, isPending: EditDeliveryManagerLoading } =
    useEditDeliveryManager();

  return (
    <>
      {AllForm({
        initialValues: {
          name: DeliveryManagerData ? DeliveryManagerData?.name : '',
          email: DeliveryManagerData ? DeliveryManagerData?.email : '',
          mobile: DeliveryManagerData
            ? String(DeliveryManagerData?.mobileNumber)?.slice(selectedCountryCode?.length)
            : null,
          profilePhoto: DeliveryManagerData
            ? DeliveryManagerData?.profilePhoto
            : '',
        },
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema,
        onSubmit: async (values: any) => {
          const modifiedValues = {
            ...values,
            name: values.name,
            email: values.email,
            mobileNumber: `${selectedCountryCode}${values.mobile}`,
          };
          try {
            if (DeliveryManagerData) {
              EditDeliveryManager(
                {
                  id: DeliveryManagerData.id || '',
                  data: modifiedValues,
                },
                {
                  onSuccess: () => {
                    onCancel();
                    // closeForm();
                    // resetForm();
                  },
                  onError: (error: any) => {
                    console.log(error);
                    // Handle error appropriately
                    // setErrors(parseJavaError(error));
                  },
                }
              );
            } else {
              CreateDeliveryManager(modifiedValues, {
                onSuccess: () => {
                  onCancel();
                  // closeForm();
                  // resetForm();
                },
                onError: (error: any) => {
                  console.log(error);
                  // setErrors(parseJavaError(error));
                },
              });
            }
          } catch (error) {
            console.log(error);
            // setErrors(parseJavaError(error));
          }
        },

        fields: [
          {
            name: 'name',
            label: 'Name:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'email',
            label: 'Email ID:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'mobile',
            label: 'Phone:',
            type: 'custom',
            isRequired: true,
            component: ({
              field,
              form,
            }: {
              field: FieldInputProps<any>;
              form: FormikProps<any>;
            }) => (
              <PhoneNumberInput
                field={field}
                form={form}
                selectedCountryCode={selectedCountryCode}
                onCountryCodeChange={setSelectedCountryCode}
              />
            )
          },
          {
            name: 'profilePhoto',
            label: 'Profile Photo:',
            type: 'file',
            isRequired: true,
            component: () => (
              <ImageUploadField
                name="profilePhoto"
                label="logo"
              />
            )
          },
        ],

        customButton: (formikProps: any) => (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                backgroundColor: 'rgb(76, 10, 39)',
                '&:hover': {
                  backgroundColor: 'rgb(76, 10, 39, 0.8)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              onClick={(e) => {
                e.preventDefault();
                // Mark all fields as touched to show validation errors
                Object.keys(formikProps.values).forEach((fieldName) => {
                  formikProps.setFieldTouched(fieldName, true, false);
                });
                // Check if there are validation errors
                if (Object.keys(formikProps.errors).length === 0) {
                  formikProps.handleSubmit();
                } else {
                  // If there are errors, do not submit
                  console.log('Validation errors:', formikProps.errors);
                }
              }}
              disabled={
                CreateDeliveryManagerLoading || EditDeliveryManagerLoading
              }
            >
              {CreateDeliveryManagerLoading || EditDeliveryManagerLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                  {DeliveryManagerData ? 'Updating...' : 'Creating...'}
                </>
              ) : DeliveryManagerData ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>

            {!DeliveryManagerData && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => formikProps.resetForm()}
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderColor: '#666',
                  color: '#666',
                  '&:hover': {
                    borderColor: '#333',
                    color: '#333',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                Reset
              </Button>
            )}
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onCancel}
              sx={{
                bgcolor: warning.main,
                color: '#fff',
                '&:hover': {
                  bgcolor: warning.dark,
                  color: '#fff',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        ),
      })()}
    </>
  );
};

export default EditDeliveryManager;
